import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiError, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Star, Timer, Coins, X, Sparkles, ShoppingBag, Flag, Gift, Zap,
} from "lucide-react";
import { DynamicIcon } from "lucide-react/dynamic";
import UserBadge from "@/components/UserBadge";

// Prize type visual metadata — mirrors the admin/type map on the backend so
// the inline prize card can render the right icon + gradient without an
// extra API call.
const PRIZE_META = {
  credits:              { label: "Credits",                   Icon: Coins,    tint: "from-blue-500 to-indigo-700" },
  banner_impressions:   { label: "Banner Impressions",        Icon: Flag,     tint: "from-fuchsia-500 to-pink-700" },
  text_ad_impressions:  { label: "Text Ad Impressions",       Icon: Sparkles, tint: "from-cyan-500 to-teal-700" },
  surf_drawn_tickets:   { label: "Surf Drawn Tickets",        Icon: Star,     tint: "from-amber-500 to-orange-700" },
  diamond_emeralds:     { label: "Diamond Emeralds",          Icon: Sparkles, tint: "from-emerald-500 to-green-800" },
  heart_chrysolite:     { label: "Heart Shaped Chrysolite",   Icon: Sparkles, tint: "from-rose-500 to-pink-800" },
};

function resolveAvatar(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

const _PoolFallback = ({ size, strokeWidth, className }) => (
  <Star size={size} strokeWidth={strokeWidth} className={className} />
);

/**
 * Render a lucide icon by its kebab-case name (received from the backend
 * verification pool). Falls back to Star while the lazy chunk loads and if
 * the name is unknown, so users always see *something* even if the pool ever
 * drifts from the frontend package.
 */
function PoolIcon({ name, size = 18, className, strokeWidth = 2 }) {
  return (
    <DynamicIcon
      name={name}
      size={size}
      strokeWidth={strokeWidth}
      className={className}
      fallback={_PoolFallback}
    />
  );
}

export default function Surf() {
  const nav = useNavigate();
  const { user, siteSettings } = useAuth();
  const isAdmin = user?.role === "admin";
  // Admin-only autosurf: skips the verification picker when the timer ends
  // and auto-claims any pending prize so the admin never has to click
  // through surfs manually. Persisted so it survives reloads.
  const [autosurf, setAutosurf] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("te_admin_autosurf") === "1";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("te_admin_autosurf", autosurf ? "1" : "0");
    }
  }, [autosurf]);
  const [session, setSession] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("scam");
  const [reportNote, setReportNote] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  // Prize Pages: when the last surf unlocked a milestone reward, this holds
  // the payload from /api/surf/complete and the iframe area swaps out for
  // the claim card. Cleared after the user claims (loads next site).
  const [pendingPrize, setPendingPrize] = useState(null);
  const [prizeBusy, setPrizeBusy] = useState(false);
  const [claimedPrize, setClaimedPrize] = useState(null);
  const claimInFlight = useRef(false);
  const timerRef = useRef(null);

  const submitReport = async () => {
    if (!session?.website?.id || reportBusy) return;
    setReportBusy(true);
    try {
      const { data } = await api.post(`/websites/${session.website.id}/report`, {
        reason: reportReason,
        note: reportNote,
      });
      if (data?.already_reported) {
        toast.info("You've already reported this website — the admin is on it.");
      } else {
        toast.success("Report sent. Thanks for helping keep the network clean.");
      }
      setReportOpen(false);
      setReportNote("");
      setReportReason("scam");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReportBusy(false);
    }
  };

  const loadNext = async () => {
    setErr(""); setReady(false);
    try {
      const { data } = await api.get("/surf/next");
      setSession(data);
      setRemaining(data.timer_seconds);
    } catch (e) {
      setErr(formatApiError(e));
      setSession(null);
    }
  };

  useEffect(() => { loadNext(); return () => clearInterval(timerRef.current); }, []);

  useEffect(() => {
    if (!session) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(timerRef.current);
          setReady(true);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [session]);

  const submit = async (given) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/surf/complete", {
        session_id: session.session_id,
        verification_answer: given,
      });
      toast.success(`+${data.credits_earned} credits earned!`);
      // Prize Pages: server may return a pending prize the user just unlocked.
      // Render it inline (below the surfbar) instead of loading the next site.
      if (data.pending_prize) {
        setPendingPrize(data.pending_prize);
        setClaimedPrize(null);
        setReady(false);
        return;
      }
      loadNext();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const claimPrize = async () => {
    if (prizeBusy || claimInFlight.current) return;
    claimInFlight.current = true;
    setPrizeBusy(true);
    try {
      const { data } = await api.post("/prize/claim");
      setClaimedPrize(data);
      toast.success(`Prize claimed: +${Number(data.amount).toLocaleString()} ${PRIZE_META[data.prize_type]?.label || data.prize_type}`);
      // Auto-continue: brief celebration flash, then load the next site so
      // the surfbar timer resumes its normal cadence without an extra click.
      setTimeout(() => {
        setPendingPrize(null);
        setClaimedPrize(null);
        loadNext();
      }, 1400);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (msg === "Prize already claimed" || msg === "No prize to claim") {
        toast.info("This prize has already been claimed.");
        setPendingPrize(null);
        loadNext();
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setPrizeBusy(false);
      claimInFlight.current = false;
    }
  };

  const targetName = session?.verification_code;

  // Autosurf auto-triggers (admin only). Fires `submit` the moment the timer
  // completes, and auto-claims any pending prize so the admin never clicks.
  useEffect(() => {
    if (!isAdmin || !autosurf) return;
    if (ready && session?.verification_code && !busy && !pendingPrize) {
      // Submit the correct answer silently.
      submit(session.verification_code);
    }
  }, [isAdmin, autosurf, ready, session?.session_id, busy, pendingPrize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAdmin || !autosurf) return;
    if (pendingPrize && !claimedPrize && !prizeBusy) {
      claimPrize();
    }
  }, [isAdmin, autosurf, pendingPrize, claimedPrize, prizeBusy]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-950 text-white">
      {/* Top surf bar */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 bg-green-600 border-b border-green-800 shadow-lg">
        <Link to="/dashboard" className="shrink-0" data-testid="surf-brand-logo">
          <img src={siteSettings?.logo_url || LOGO_URL} alt={siteSettings?.site_name || "logo"} className="h-8 sm:h-10 w-auto" />
        </Link>
        <div className="flex items-center gap-2 bg-green-800/60 px-2.5 py-1 rounded-full">
          <Timer size={16} className="text-yellow-300" />
          <div className="text-xl font-black tabular-nums" data-testid="surf-timer">{remaining}s</div>
        </div>

        {/* Admin autosurf toggle — bypasses verification picking + prize
            claim clicks so admins can passively validate the surf engine. */}
        {isAdmin && (
          <button
            data-testid="autosurf-toggle"
            onClick={() => setAutosurf((v) => !v)}
            title={autosurf ? "Autosurf is ON — admin bypass active" : "Enable autosurf (admin bypass)"}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-black uppercase tracking-widest shadow-sm transition-colors duration-200 ${autosurf ? "bg-amber-300 text-amber-950 ring-2 ring-amber-100 animate-pulse" : "bg-green-900/60 text-green-100 hover:bg-green-900"}`}
          >
            <Zap size={14} className={autosurf ? "fill-current" : ""} />
            {autosurf ? "Autosurf ON" : "Autosurf"}
          </button>
        )}


        {/* Site owner + title */}
        {(() => {
          const owner = session?.website?.owner;
          const ownerAvatar = resolveAvatar(owner?.avatar_url);
          const ownerLetter = (owner?.username || "?").slice(0, 1).toUpperCase();
          return (
            <div className="min-w-0 flex items-center gap-2 max-w-[180px] sm:max-w-[300px]" data-testid="surf-owner-card">
              {owner ? (
                <Link
                  to={`/u/${owner.id}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  title={`View @${owner.username}'s profile`}
                  className="shrink-0"
                  data-testid="surf-owner-avatar"
                >
                  {ownerAvatar ? (
                    <img src={ownerAvatar} alt={owner.username} className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover ring-2 ring-yellow-300/70" />
                  ) : (
                    <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-800 text-white flex items-center justify-center font-black text-sm ring-2 ring-yellow-300/70">
                      {ownerLetter}
                    </div>
                  )}
                </Link>
              ) : (
                <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-slate-800 shrink-0" />
              )}
              <div className="min-w-0 hidden sm:block">
                <div className="text-[10px] uppercase tracking-wider text-white/70 leading-none">
                  {owner ? "Surfing site by" : "Surfing"}
                </div>
                {owner ? (
                  <Link
                    to={`/u/${owner.id}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-xs font-black text-yellow-200 hover:underline truncate inline-flex items-center gap-1"
                    data-testid="surf-owner-name"
                  >
                    @{owner.username}
                    <UserBadge user={owner} size={11} />
                    {owner.membership && owner.membership !== "Free" && (
                      <span className="ml-1 text-[9px] font-bold uppercase tracking-widest text-yellow-300">{owner.membership}</span>
                    )}
                  </Link>
                ) : null}
                <div className="text-[11px] font-semibold text-white/90 truncate">
                  {session?.website?.title}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Inline verification — shows only after timer hits 0. Image-only. */}
        {ready && session && (
          <div className="flex flex-wrap items-center gap-2 flex-1 justify-center order-3 sm:order-none w-full sm:w-auto">
            <div className="flex items-center gap-1.5 bg-slate-900/80 text-yellow-300 rounded-full pl-3 pr-2 py-1">
              <span className="text-[10px] uppercase tracking-widest text-white/70">Match</span>
              {targetName && <PoolIcon name={targetName} size={20} strokeWidth={2.5} />}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              {(session.verification_options || []).map((name) => {
                return (
                  <button
                    key={name}
                    data-testid={`verify-image-${name}`}
                    disabled={busy}
                    onClick={() => submit(name)}
                    className="w-9 h-9 rounded-full bg-white hover:bg-yellow-100 hover:scale-110 border-2 border-transparent hover:border-yellow-400 flex items-center justify-center transition-all disabled:opacity-50"
                    title={`Pick ${name}`}
                  >
                    <PoolIcon name={name} size={18} className="text-slate-800" strokeWidth={2} />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!ready && (
          <div className="flex-1 text-center text-white/80 text-xs sm:text-sm min-w-0">
            <span className="truncate inline-block max-w-full">Watching…<span className="hidden md:inline text-white/60"> verification unlocks at 0s</span></span>
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          {/* Prize meter — sits next to the Buy buttons so progress toward the
              next reward is right where the user's eye goes for actions. */}
          {session?.next_prize && (
            <div
              data-testid="surf-prize-meter"
              title={`Next prize unlocks at ${session.next_prize.target_surfs.toLocaleString()} sites — ${session.next_prize.remaining} to go`}
              className="hidden sm:flex items-center gap-2 bg-gradient-to-br from-yellow-400 via-orange-400 to-pink-500 rounded-full pl-2 pr-3 py-1 shadow-md min-w-[240px] max-w-[320px]"
            >
              <Gift size={16} className="text-white drop-shadow shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between text-[9px] font-black uppercase tracking-widest text-white/90 leading-none">
                  <span data-testid="surf-prize-meter-label" className="truncate">Next prize</span>
                  <span data-testid="surf-prize-meter-remaining" className="tabular-nums pl-1 shrink-0">{session.next_prize.remaining} to go</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-black/25 overflow-hidden">
                  <div
                    data-testid="surf-prize-meter-fill"
                    className="h-full bg-white rounded-full transition-all duration-500"
                    style={{ width: `${Math.max(3, Math.min(100, Math.round(session.next_prize.progress * 100)))}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Buy buttons — stacked, compact so they don't crowd the surfbar */}
          <div className="flex flex-col gap-1">
            <button
              data-testid="surf-buy-credits-btn"
              onClick={() => nav("/buy-credits")}
              title="Buy Credits"
              className="inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-slate-900 font-black text-[9px] uppercase tracking-wider px-2 py-0.5 leading-tight shadow-[0_2px_0_rgba(154,52,18,0.9)] hover:shadow-[0_1px_0_rgba(154,52,18,0.9)] hover:translate-y-[1px] transition-all duration-150 border border-orange-300/60 whitespace-nowrap"
            >
              <Coins size={10} className="drop-shadow" />
              Buy Credits
            </button>

            <button
              data-testid="surf-buy-upgrades-btn"
              onClick={() => nav("/buy-upgrades")}
              title="Buy Upgrades"
              className="inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 hover:from-violet-400 hover:to-fuchsia-500 text-white font-black text-[9px] uppercase tracking-wider px-2 py-0.5 leading-tight shadow-[0_2px_0_rgba(107,33,168,0.9)] hover:shadow-[0_1px_0_rgba(107,33,168,0.9)] hover:translate-y-[1px] transition-all duration-150 border border-fuchsia-300/40 whitespace-nowrap"
            >
              <ShoppingBag size={10} className="drop-shadow" />
              Buy Upgrades
            </button>
          </div>

          {/* Rolling bonus progress — sits right next to the red Exit button */}
          {session && Number(session.bonus_threshold || 0) > 0 && Number(session.bonus_credits || 0) > 0 && (() => {
            const surfs = Number(session.bonus_cycle_surfs || 0);
            const threshold = Number(session.bonus_threshold);
            const bonus = Number(session.bonus_credits);
            const remainingSites = Math.max(0, threshold - surfs);
            const pct = Math.min(100, Math.round((surfs / threshold) * 100));
            return (
              <div
                className="flex items-center gap-2 bg-yellow-400/95 text-yellow-950 px-2.5 py-1 rounded-full shadow-md border border-yellow-500 w-[190px] sm:w-[220px]"
                title={`${surfs} of ${threshold} sites surfed this bonus cycle. Surf ${remainingSites} more to earn +${bonus} bonus credits.`}
                data-testid="surf-bonus-progress"
              >
                <Sparkles size={14} className="shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2 text-[10px] font-black uppercase tracking-widest leading-none">
                    <span data-testid="surf-bonus-counter">{surfs}/{threshold}</span>
                    <span className="text-[9px] font-bold">
                      {remainingSites === 0 ? "Bonus ready!" : `+${bonus} in ${remainingSites}`}
                    </span>
                  </div>
                  <div className="mt-1 h-1 w-full bg-yellow-800/25 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-950 transition-all duration-300"
                      style={{ width: `${pct}%` }}
                      data-testid="surf-bonus-fill"
                    />
                  </div>
                </div>
              </div>
            );
          })()}

          <button
            data-testid="surf-report-btn"
            onClick={() => setReportOpen(true)}
            title="Report this website (virus, frame breaker, scam…)"
            className="rounded-full bg-slate-900 hover:bg-black text-white text-xs px-2.5 sm:px-3 py-1.5 inline-flex items-center gap-1 transition-colors font-black uppercase tracking-wider ring-1 ring-black/40"
          >
            <Flag size={14} className="text-red-500 fill-red-500" strokeWidth={2.5} />
            <span className="hidden xs:inline">Report</span>
          </button>

          <button
            data-testid="surf-exit-btn"
            onClick={() => nav("/dashboard")}
            className="rounded-full bg-red-500 hover:bg-red-600 text-white text-xs px-2.5 sm:px-3 py-1.5 inline-flex items-center gap-1 transition-colors"
          >
            <X size={14} /> <span className="hidden xs:inline">Exit</span>
          </button>
        </div>
      </div>

      {/* Report modal */}
      {reportOpen && (
        <div
          data-testid="surf-report-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={() => setReportOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-xs font-black uppercase tracking-widest text-yellow-600 dark:text-yellow-400">Report website</div>
                <div className="text-lg font-black text-slate-900 dark:text-white mt-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
                  {session?.website?.title || "This site"}
                </div>
                <div className="text-xs text-slate-500 truncate max-w-full">{session?.website?.url}</div>
              </div>
              <button
                data-testid="surf-report-close"
                onClick={() => setReportOpen(false)}
                aria-label="Close"
                className="rounded-full w-8 h-8 inline-flex items-center justify-center bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300"
              >
                <X size={14} />
              </button>
            </div>

            <label className="block text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">
              What's wrong with this site?
            </label>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { v: "virus",         label: "Virus / malware" },
                { v: "frame_breaker", label: "Frame breaker" },
                { v: "scam",          label: "Scam / phishing" },
                { v: "adult",         label: "Adult / NSFW" },
                { v: "broken",        label: "Broken / 404" },
                { v: "other",         label: "Other" },
              ].map((r) => (
                <button
                  key={r.v}
                  data-testid={`surf-report-reason-${r.v}`}
                  onClick={() => setReportReason(r.v)}
                  className={`rounded-xl border px-3 py-2 text-xs font-bold text-left transition ${
                    reportReason === r.v
                      ? "border-yellow-500 bg-yellow-50 text-yellow-900 dark:bg-yellow-500/15 dark:text-yellow-200"
                      : "border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-yellow-300"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <label className="block text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">
              Note <span className="text-slate-400 normal-case font-medium">(optional, 500 chars)</span>
            </label>
            <textarea
              data-testid="surf-report-note"
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value.slice(0, 500))}
              placeholder="Anything the admin should know…"
              rows={3}
              className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
            />

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setReportOpen(false)}
                className="rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs font-bold px-4 py-2"
              >
                Cancel
              </button>
              <button
                data-testid="surf-report-submit"
                onClick={submitReport}
                disabled={reportBusy}
                className="inline-flex items-center gap-1 rounded-full bg-yellow-500 hover:bg-yellow-400 text-yellow-950 text-xs font-black uppercase tracking-widest px-4 py-2 disabled:opacity-60"
              >
                <Flag size={12} /> {reportBusy ? "Sending…" : "Send report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iframe fills rest — unless a prize is pending, in which case the
          Prize Page card takes over this area while the surfbar stays. */}
      <div className="relative flex-1 bg-black">
        {err && !pendingPrize && (
          <div className="absolute inset-0 flex items-center justify-center p-8">
            <div className="max-w-md rounded-2xl bg-slate-900 border border-slate-700 p-6 text-center">
              <div className="text-lg font-bold text-red-400 mb-2">Cannot load surf</div>
              <div className="text-sm text-slate-300" data-testid="surf-error">{err}</div>
              <button onClick={() => nav("/dashboard")} className="mt-4 rounded-full bg-yellow-400 hover:bg-yellow-300 text-yellow-950 font-bold px-4 py-2 text-sm">Back to Dashboard</button>
            </div>
          </div>
        )}
        {pendingPrize ? (
          <div
            data-testid="surf-prize-panel"
            className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-yellow-400 via-orange-400 to-pink-500 p-4 overflow-auto"
          >
            <div className="pointer-events-none absolute inset-0 opacity-30">
              <Gift size={80} className="absolute top-8 left-8 text-white rotate-12" />
              <Gift size={60} className="absolute top-16 right-10 text-white -rotate-12" />
              <Gift size={70} className="absolute bottom-10 left-16 text-white rotate-45" />
              <Gift size={50} className="absolute bottom-20 right-20 text-white -rotate-45" />
            </div>
            <div className="relative w-full max-w-lg rounded-3xl bg-white text-slate-900 shadow-2xl overflow-hidden">
              {(() => {
                // Before claim we intentionally hide the prize type + amount
                // so it feels like a mystery reveal. Only the milestone is
                // acknowledged — then the Claim button unlocks the full card.
                const meta = PRIZE_META[pendingPrize.prize_type] || { label: pendingPrize.prize_type_label || pendingPrize.prize_type, Icon: Gift, tint: "from-yellow-400 to-orange-600" };
                const Icon = meta.Icon;
                const headerTint = claimedPrize ? meta.tint : "from-yellow-500 via-orange-500 to-rose-500";
                return (
                  <>
                    <div className={`bg-gradient-to-br ${headerTint} p-6 sm:p-8 text-center text-white`}>
                      <div className="inline-flex items-center justify-center h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-white/20 backdrop-blur-sm mb-3">
                        {claimedPrize ? <Icon size={40} strokeWidth={2.5} /> : <Gift size={44} strokeWidth={2.5} />}
                      </div>
                      <div className="text-[10px] sm:text-xs font-black uppercase tracking-[0.3em] text-white/80">Milestone reached</div>
                      <div className="text-3xl sm:text-5xl font-black mt-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
                        {claimedPrize ? "🎉 Prize claimed!" : "🎁 Congratulations!"}
                      </div>
                    </div>
                    <div className="p-6 sm:p-8 text-center">
                      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">You reached the</div>
                      <div className="text-base sm:text-lg text-slate-800">
                        <span className="font-black">{Number(pendingPrize.milestone).toLocaleString()}</span>-site milestone
                      </div>
                      <div className="my-5 sm:my-6 rounded-2xl bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 p-5 sm:p-6">
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                          {claimedPrize ? "Your prize" : "Your mystery prize"}
                        </div>
                        {claimedPrize ? (
                          <>
                            <div className="mt-1 text-4xl sm:text-5xl font-black text-slate-900 inline-flex items-baseline gap-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }} data-testid="surf-prize-amount">
                              {Number(pendingPrize.amount).toLocaleString()}
                              <span className="text-lg sm:text-xl font-bold text-slate-600">{meta.label}</span>
                            </div>
                            {pendingPrize.name && <div className="mt-2 text-sm font-bold text-slate-700" data-testid="surf-prize-name">{pendingPrize.name}</div>}
                            {pendingPrize.recurring && (
                              <div className="mt-3 text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 rounded-full inline-block px-3 py-1">Recurring reward</div>
                            )}
                          </>
                        ) : (
                          <div className="mt-2" data-testid="surf-prize-hidden">
                            <div className="text-5xl sm:text-6xl font-black text-slate-900 tracking-widest select-none" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
                              ? ? ?
                            </div>
                            <div className="mt-2 text-xs text-slate-500">Click <span className="font-black">Claim</span> to reveal</div>
                          </div>
                        )}
                      </div>
                      {!claimedPrize ? (
                        <button
                          data-testid="surf-prize-claim-btn"
                          onClick={claimPrize}
                          disabled={prizeBusy}
                          className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-black px-8 py-3 sm:py-3.5 text-base shadow-lg disabled:opacity-60 transition-colors duration-200"
                        >
                          <Gift size={18} /> {prizeBusy ? "Revealing…" : "Claim your prize"}
                        </button>
                      ) : (
                        <div
                          data-testid="surf-prize-loading-next"
                          className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white font-black px-8 py-3 sm:py-3.5 text-base shadow-lg"
                        >
                          <Sparkles size={18} className="animate-pulse" />
                          Loading next site…
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        ) : (
          session && (
            <iframe
              data-testid="surf-iframe"
              key={session.session_id}
              src={session.website.url}
              title="surf-frame"
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin allow-forms"
            />
          )
        )}
      </div>
    </div>
  );
}
